import { isDirectory } from './chunk-53IMCORZ.mjs';
import { __name } from './chunk-Q72B4Q5Z.mjs';
import os from 'node:os';
import path from 'node:path';

function isWindows() {
  return /^win/i.test(process.platform);
}
__name(isWindows, "isWindows");
function isMacOS() {
  return /^darwin$/i.test(process.platform);
}
__name(isMacOS, "isMacOS");
function isNonEmpty(value) {
  return !!value;
}
__name(isNonEmpty, "isNonEmpty");
function getEnv(name) {
  return process.env[name];
}
__name(getEnv, "getEnv");
function normalizePath(p) {
  return p ? path.normalize(path.join(p, ".")) : void 0;
}
__name(normalizePath, "normalizePath");
function homeDir() {
  if (isWindows()) {
    const priorityList = [
      os.homedir(),
      getEnv("USERPROFILE"),
      getEnv("HOME"),
      // NOTE: `os-paths@7` uses `||` here (not `&&`), so a home directory is
      // derived even when only one of HOMEDRIVE/HOMEPATH is set. This is
      // preserved deliberately for byte-for-byte parity — see the parity
      // tests, which compare this branch against the real package.
      getEnv("HOMEDRIVE") || getEnv("HOMEPATH") ? path.join(getEnv("HOMEDRIVE") ?? "", getEnv("HOMEPATH") ?? "") : void 0
    ];
    return normalizePath(priorityList.find(isNonEmpty));
  }
  return normalizePath(os.homedir() || getEnv("HOME"));
}
__name(homeDir, "homeDir");
function joinToBase(base, segments) {
  return base ? path.join(base, ...segments) : void 0;
}
__name(joinToBase, "joinToBase");
function tempDir() {
  if (isWindows()) {
    const fallback2 = "C:\\Temp";
    const priorityListLazy = [
      () => os.tmpdir(),
      () => getEnv("TEMP"),
      () => getEnv("TMP"),
      () => joinToBase(getEnv("LOCALAPPDATA"), ["Temp"]),
      () => joinToBase(homeDir(), ["AppData", "Local", "Temp"]),
      () => joinToBase(getEnv("ALLUSERSPROFILE"), ["Temp"]),
      () => joinToBase(getEnv("SystemRoot"), ["Temp"]),
      () => joinToBase(getEnv("windir"), ["Temp"]),
      () => joinToBase(getEnv("SystemDrive"), ["\\", "Temp"])
    ];
    const found = priorityListLazy.find((fn) => isNonEmpty(fn()));
    return found && normalizePath(found()) || fallback2;
  }
  const fallback = "/tmp";
  const priorityList = [
    os.tmpdir(),
    getEnv("TMPDIR"),
    getEnv("TEMP"),
    getEnv("TMP")
  ];
  return normalizePath(priorityList.find(isNonEmpty)) || fallback;
}
__name(tempDir, "tempDir");
function baseDir() {
  return homeDir() || tempDir();
}
__name(baseDir, "baseDir");
function valOrPath(value, segments) {
  return value || path.join(...segments);
}
__name(valOrPath, "valOrPath");
function windowsAppData() {
  return valOrPath(getEnv("APPDATA"), [baseDir(), "AppData", "Roaming"]);
}
__name(windowsAppData, "windowsAppData");
function windowsLocalAppData() {
  return valOrPath(getEnv("LOCALAPPDATA"), [baseDir(), "AppData", "Local"]);
}
__name(windowsLocalAppData, "windowsLocalAppData");
function xdgConfig() {
  if (isMacOS()) {
    return valOrPath(getEnv("XDG_CONFIG_HOME"), [
      baseDir(),
      "Library",
      "Preferences"
    ]);
  }
  if (isWindows()) {
    return valOrPath(getEnv("XDG_CONFIG_HOME"), [
      windowsAppData(),
      "xdg.config"
    ]);
  }
  return valOrPath(getEnv("XDG_CONFIG_HOME"), [baseDir(), ".config"]);
}
__name(xdgConfig, "xdgConfig");
function xdgCache() {
  if (isMacOS()) {
    return valOrPath(getEnv("XDG_CACHE_HOME"), [
      baseDir(),
      "Library",
      "Caches"
    ]);
  }
  if (isWindows()) {
    return valOrPath(getEnv("XDG_CACHE_HOME"), [
      windowsLocalAppData(),
      "xdg.cache"
    ]);
  }
  return valOrPath(getEnv("XDG_CACHE_HOME"), [baseDir(), ".cache"]);
}
__name(xdgCache, "xdgCache");
function xdgAppPaths(name) {
  const segment = path.parse(name).name;
  return {
    config: /* @__PURE__ */ __name(() => path.join(xdgConfig(), segment), "config"),
    cache: /* @__PURE__ */ __name(() => path.join(xdgCache(), segment), "cache")
  };
}
__name(xdgAppPaths, "xdgAppPaths");

// src/global-wrangler-config-path.ts
function getGlobalConfigPath({
  appName = "wrangler",
  leadingDot = true,
  useLegacyHomeDir = true
} = {}) {
  const dirName = `${leadingDot ? "." : ""}${appName}`;
  const configDir = xdgAppPaths(dirName).config();
  if (useLegacyHomeDir) {
    const legacyConfigDir = path.join(os.homedir(), dirName);
    if (isDirectory(legacyConfigDir)) {
      return legacyConfigDir;
    }
  }
  return configDir;
}
__name(getGlobalConfigPath, "getGlobalConfigPath");
function getGlobalWranglerCachePath() {
  return xdgAppPaths(".wrangler").cache();
}
__name(getGlobalWranglerCachePath, "getGlobalWranglerCachePath");

export { getGlobalConfigPath, getGlobalWranglerCachePath };
